/*
  Delete Node Confirmation Modal

  Matches the old journey editor's simple delete confirmation style.
  Shows total impact (orphaned nodes) but keeps the UI clean and minimal.
*/

import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  Modal,
  Button,
  ButtonVariant,
} from '@usealloy/component-library';
import type { Node, Edge } from '../../types';

interface DeleteNodeConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  nodesToDelete: Node[];
  allNodes: Record<string, Node>;
  allEdges: Record<string, Edge>;
}

const ModalBody = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
`;

const NodeCount = styled.div`
  margin: 12px 0 0 0;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  color: #6b7280;
  
  strong {
    color: #1f2937;
    font-weight: 600;
  }
`;

/**
 * Gets parent nodes for a given node
 */
function getParentNodes(
  nodeId: string,
  allEdges: Record<string, Edge>,
): string[] {
  return Object.values(allEdges)
    .filter(edge => edge.to.node === nodeId)
    .map(edge => edge.from.node);
}

/**
 * Finds nodes that will be orphaned (and thus deleted) when deleting the specified nodes.
 * This matches the new deletion behavior: only children with no other parents are deleted.
 *
 * Uses an iterative approach to maintain a single deletion set throughout traversal,
 * ensuring that descendants orphaned by the combination of multiple upstream deletions
 * are correctly detected.
 */
function findOrphanedNodes(
  nodeIds: string[],
  allNodes: Record<string, Node>,
  allEdges: Record<string, Edge>,
): string[] {
  // Track all nodes that will be deleted (initial + orphaned)
  const nodesToDelete = new Set<string>(nodeIds);
  const orphaned = new Set<string>();

  // Queue of nodes to process for finding their orphaned children
  const queue = [...nodeIds];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;

    // Find children of current node
    const children = Object.values(allEdges)
      .filter(edge => edge.from.node === nodeId)
      .map(edge => edge.to.node);

    // Check each child to see if it will be orphaned
    children.forEach(childId => {
      // Skip if already processed
      if (nodesToDelete.has(childId) || orphaned.has(childId)) return;

      // Get all parents of this child
      const parents = getParentNodes(childId, allEdges);

      // Check if all parents are being deleted (considering the full deletion set)
      const hasRemainingParent = parents.some(
        parentId => !nodesToDelete.has(parentId),
      );

      // If no remaining parents, this child will be orphaned
      if (!hasRemainingParent) {
        orphaned.add(childId);
        nodesToDelete.add(childId); // Add to deletion set for subsequent checks
        queue.push(childId); // Process this node's children
      }
    });
  }

  return Array.from(orphaned);
}

const DeleteNodeConfirmModal: React.FC<DeleteNodeConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  nodesToDelete,
  allNodes,
  allEdges,
}) => {
  const orphanedNodeIds = useMemo(() => findOrphanedNodes(
    nodesToDelete.map(n => n.id),
    allNodes,
    allEdges,
  ), [nodesToDelete, allNodes, allEdges]);

  const orphanedNodes = orphanedNodeIds.map(id => allNodes[id]).filter(Boolean);
  const totalNodesToDelete = nodesToDelete.length + orphanedNodes.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Delete Node"
      size="small"
      hasCloseIcon={false}
      secondaryAction={(
        <Button onClick={onClose}>
          Cancel
        </Button>
      )}
      primaryAction={(
        <Button
          variant={ButtonVariant.primary}
          onClick={onConfirm}
        >
          Delete
        </Button>
      )}
    >
      <ModalBody>
        Are you sure you want to delete {nodesToDelete.length === 1 ? 'this node' : 'these nodes'}?
        By doing so, all the subsequent logic associated with it will be deleted too.

        {totalNodesToDelete > nodesToDelete.length && (
          <NodeCount>
            <strong>{totalNodesToDelete} nodes</strong> will be deleted
            ({nodesToDelete.length} selected + {orphanedNodes.length} orphaned)
          </NodeCount>
        )}
      </ModalBody>
    </Modal>
  );
};

export default DeleteNodeConfirmModal;
